import type { LibraryResponses } from "@tfg/types/endpoints/library"
import type { SourceContextGraphNodeDto } from "~/gen/api/types"

import {
  buildForkOriginCue,
  isSourceContextGraphNode,
  type BrandTraceabilityGraphNode,
  type ForkOriginCue,
} from "~/pages/Brand/Products/Details/TraceabilityGraph/traceability-graph.view-model"

export type CustomFieldForkBranch = {
  readonly contextId: string
  readonly anchorItemId: number
  readonly originCue: ForkOriginCue
  readonly operationIdsByItemId: ReadonlyMap<number, ReadonlySet<number>>
}

export type CustomFieldTreeRoot =
  | { readonly kind: "product"; readonly product: LibraryResponses.ProductDetails }
  | { readonly kind: "component"; readonly component: LibraryResponses.EnrichedComponent }
  | { readonly kind: "material"; readonly material: LibraryResponses.EnrichedMaterial }

type GetCustomFieldForkBranchesArgs = {
  readonly nodes: readonly BrandTraceabilityGraphNode[]
  readonly translateItemType: (type: SourceContextGraphNodeDto["type"]) => string
  readonly translateActivity: (activityName: string) => string
}

export function getCustomFieldForkBranches({
  nodes,
  translateItemType,
  translateActivity,
}: GetCustomFieldForkBranchesArgs): CustomFieldForkBranch[] {
  const nodesById = new Map(nodes.map((node) => [node.id, node]))
  const forkRoots = nodes.filter(isSourceContextGraphNode)

  return forkRoots.map((forkRoot) => {
    const operationIdsByItemId = new Map<number, Set<number>>()

    const visit = (node: BrandTraceabilityGraphNode): void => {
      if (node.operation !== "not-entered-yet") {
        const itemOperationIds =
          operationIdsByItemId.get(node.itemId) ?? new Set<number>()
        itemOperationIds.add(node.operation.id)
        operationIdsByItemId.set(node.itemId, itemOperationIds)
      }

      for (const childId of node.childrenRef) {
        const childNode = nodesById.get(childId)
        if (childNode === undefined) {
          continue
        }
        if (isSourceContextGraphNode(childNode)) {
          continue
        }
        visit(childNode)
      }
    }

    visit(forkRoot)

    return {
      contextId: forkRoot.contextId,
      anchorItemId: forkRoot.itemId,
      originCue: buildForkOriginCue({
        forkRoot,
        translateItemType,
        translateActivity,
      }),
      operationIdsByItemId,
    }
  })
}

export function findCustomFieldTreeRoot(
  product: LibraryResponses.ProductDetails,
  anchorItemId: number
): CustomFieldTreeRoot | undefined {
  if (product.item.id === anchorItemId) {
    return { kind: "product", product }
  }

  for (const component of product.components) {
    if (component.item.id === anchorItemId) {
      return { kind: "component", component }
    }

    for (const material of component.materials) {
      if (material.item.id === anchorItemId) {
        return { kind: "material", material }
      }
    }
  }

  return undefined
}

export function filterOperationsByIds(
  operations: readonly LibraryResponses.EnrichedOperation[],
  operationIds: ReadonlySet<number> | undefined
): LibraryResponses.EnrichedOperation[] {
  if (operationIds === undefined) {
    return []
  }

  return operations.filter((operation) => operationIds.has(operation.id))
}
